import { Trans } from '@lingui/macro';
import { Box, Tooltip, Typography } from '@mui/material';
import { type FC } from 'react';

/** Una posición que **nunca se reportó**: el movimiento es anterior al primer reporte. */
export const UNKNOWN_POSITION = '??';

interface Props {
  /** Posición de la que partió, ya desempaquetada. `null` = la tupla no existe. */
  from: number | null;
  /** Posición a la que llegó. `null` = la tupla no existe, o sea la operación no terminó. */
  to: number | null;
  /**
   * Posición pedida. Es lo que la celda muestra mientras `to` no exista. `null` en una operación
   * manual —la que abre el operario en terreno—, que no tiene destino solicitado (AMARU-862): ahí
   * no hay número que anticipar y el hueco se dice con la palabra, igual que en `detailed`.
   */
  requested: number | null;
  format: (value: number) => string;
  /**
   * La variante del diálogo, que lleva la posición solicitada en su **propia** columna.
   *
   * Cambia qué se dice cuando la operación está en curso: acá «Pendiente», porque el número
   * solicitado ya se lee en la celda de al lado y repetirlo con un asterisco sería decir dos veces
   * lo mismo en la misma fila. En el widget, que no tiene esa columna, el asterisco es el único
   * número que la fila puede ofrecer.
   *
   * Y de paso el diálogo se queda sin el `Tooltip`: en una superficie de siete columnas el dato
   * está escrito, no escondido — que es la razón de ser de este diálogo.
   */
  detailed?: boolean;
  /**
   * La operación **terminó**. Con `to` nulo es un cierre sin movimiento —una solicitud en cola
   * cancelada o vencida—: no hay posición final que esperar, y «Pendiente» diría lo contrario.
   */
  closed?: boolean;
}

/**
 * La celda de posición de una fila de movimiento.
 *
 * **Los dos huecos de esta fila son dos**, y antes eran el mismo `??`:
 *
 * - `from` nulo es un dato que **no existe y no va a existir** — el movimiento es anterior al
 *   primer reporte de posición. Eso sí es `??`.
 * - `to` nulo es una operación **en curso**: el dato está por llegar. Ahí la celda muestra la
 *   posición **solicitada** con un asterisco, que es el único número que la fila puede ofrecer, y
 *   el tooltip dice que no es la final. Antes mostraba `??` mientras la fecha de la misma fila
 *   decía «Pendiente»: la fila afirmaba a la vez que el dato se perdió y que está por llegar.
 *   En `detailed` ese mismo hueco se dice con la palabra —ver la prop.
 *
 * **`null` significa «la tupla no existe», no «el valor es cero».** Quien la construye
 * desempaqueta con `!= null` sobre la tupla, así que una posición de **0 cm** —compuerta cerrada,
 * un valor perfectamente válido— llega como `0` y se pinta como posición final. Esa distinción es
 * la que un `||` o un `!!` mal puesto rompe en silencio, y es lo que fija el test.
 *
 * Vive en su propio archivo y no inline en el `map` de la tabla para poder probarla sin levantar
 * Apollo, redux y el contexto de auth que los hooks del widget arrastran.
 */
export const MovementPosition: FC<Props> = ({
  from,
  to,
  requested,
  format,
  detailed = false,
  closed = false,
}) => (
  <Typography
    sx={{ fontSize: 11.5, textAlign: 'center', whiteSpace: 'nowrap', color: 'text.primary' }}
  >
    {from != null ? format(from) : UNKNOWN_POSITION} →{' '}
    {to != null ? (
      format(to)
    ) : closed ? (
      <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        <Trans>Sin movimiento</Trans>
      </Box>
    ) : detailed || requested == null ? (
      // Atenuado y en cursiva, igual que la fecha de finalización de la misma fila: «Pendiente» no
      // es una posición, es el hueco donde va a ir una, y leerlo con el peso de un valor lo
      // confunde con uno.
      <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        <Trans>Pendiente</Trans>
      </Box>
    ) : (
      <Tooltip title={<Trans>Posición solicitada — la operación aún no termina</Trans>}>
        {/* `tabIndex` para que el tooltip abra con teclado: MUI no lo agrega a un `span` pelado. */}
        <Box component="span" sx={{ cursor: 'help' }} tabIndex={0}>
          {format(requested)}*
        </Box>
      </Tooltip>
    )}
  </Typography>
);
